import { TooltipProvider } from "@/components/ui/tooltip";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Home from "./pages/Home";
import SEO from "./components/SEO";
import { LanguageProvider, stripLangPrefix } from "./i18n/LanguageProvider";
import type { Language } from "./i18n/types";

const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Platform = lazy(() => import("./pages/Platform"));
const Solutions = lazy(() => import("./pages/Solutions"));
const AssetOwners = lazy(() => import("./pages/solutions/AssetOwners"));
const Developers = lazy(() => import("./pages/solutions/Developers"));
const StorageOptimization = lazy(() => import("./pages/solutions/StorageOptimization"));
const CarbonValue = lazy(() => import("./pages/solutions/CarbonValue"));
const EsgIntelligence = lazy(() => import("./pages/solutions/EsgIntelligence"));
const OMIntelligence = lazy(() => import("./pages/solutions/OMIntelligence"));
const ProjectCapital = lazy(() => import("./pages/Investors"));
const ValidationMethodology = lazy(() => import("./pages/ValidationMethodology"));
const Integrations = lazy(() => import("./pages/Integrations"));
const SolarAssetManagementSoftware = lazy(() => import("./pages/SolarAssetManagementSoftware"));
const Resources = lazy(() => import("./pages/Resources"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const AccessibilityStatement = lazy(() => import("./pages/AccessibilityStatement"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));

const Toaster = lazy(() => import("@/components/ui/toaster").then((module) => ({ default: module.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((module) => ({ default: module.Toaster })));
const AdvancedModeCallout = lazy(() =>
  import("@/components/SavingsCalculator").then((module) => ({ default: module.AdvancedModeCallout }))
);
const CarbonModeCallout = lazy(() =>
  import("@/components/CarbonCalculator").then((module) => ({ default: module.CarbonModeCallout }))
);

const RouteToasters = () => {
  const { pathname } = useLocation();
  const basePath = stripLangPrefix(pathname);
  const usesFeedback = basePath === "/contact" || basePath === "/project-capital";

  if (!usesFeedback) return null;

  return (
    <Suspense fallback={null}>
      <Toaster />
      <Sonner />
    </Suspense>
  );
};

/** Route tree shared by both languages. Paths are relative to the language root. */
const SiteRoutes = () => (
  <Suspense fallback={null}>
    <Routes>
      <Route index element={<Home />} />
      <Route path="how-it-works" element={<HowItWorks />} />
      <Route path="platform" element={<Platform />} />
      <Route path="solutions" element={<Solutions />} />
      <Route path="solutions/asset-owners" element={<AssetOwners />} />
      <Route path="solutions/developers" element={<Developers />} />
      <Route path="solutions/storage" element={<StorageOptimization />} />
      <Route path="solutions/carbon-value" element={<CarbonValue />} />
      <Route path="solutions/esg" element={<EsgIntelligence />} />
      <Route path="solutions/om-intelligence" element={<OMIntelligence />} />
      <Route path="project-capital" element={<ProjectCapital />} />
      <Route path="validation-methodology" element={<ValidationMethodology />} />
      <Route path="integrations" element={<Integrations />} />
      <Route path="solar-asset-management-software" element={<SolarAssetManagementSoftware />} />
      <Route path="resources" element={<Resources />} />
      <Route path="about" element={<About />} />
      <Route path="contact" element={<Contact />} />
      <Route path="unsubscribe" element={<Unsubscribe />} />
      <Route path="accessibility-statement" element={<AccessibilityStatement />} />
      <Route path="privacy-policy" element={<PrivacyPolicy />} />
      <Route path="terms-of-service" element={<TermsOfService />} />
      <Route path="cookie-policy" element={<CookiePolicy />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

const LocalizedSite = ({ lang }: { lang: Language }) => (
  <LanguageProvider lang={lang}>
    <Suspense fallback={null}>
      <AdvancedModeCallout />
      <CarbonModeCallout />
    </Suspense>
    <SiteRoutes />
  </LanguageProvider>
);

const App = () => (
  <TooltipProvider>
    <BrowserRouter>
      <SEO />
      <RouteToasters />
      <Routes>
        <Route path="/he/*" element={<LocalizedSite lang="he" />} />
        <Route path="/*" element={<LocalizedSite lang="en" />} />
      </Routes>
    </BrowserRouter>
  </TooltipProvider>
);

export default App;
